/* @flow */
/* eslint-env es6,browser */

import * as u from '../utils';
import * as jsonp from '../jsonp';

/**
 * @description Implements functionality for Sovrn header bidding
 * @exports bidders/sovrnbid
 * @requires {@link module:utils}
 * @requires {@link module:jsonp}
 */
class _sovrnbid {
  _name: string;
  _bInited: 1 | 0 | -1;
  _DefConfig: sovrnConfig;
  _config: sovrnConfig;
  _excludeList: Array<string>;
  _bidResponses: Object;
  _bidRequest: Object;

  //Member variable default initialization in constructor
  constructor(){
    this._name = 'sovrn';
    this._bInited = u.F_NOT_INITED;
    this._DefConfig = u.extend(true, this._DefConfig, {
      positions : [],
      exclude : '',
      bidUrl : '',
      timeout : 2000,
      qpKey : 'SOV_BID', //not used only per position qpKey is used
      cVar : 'SOV_VAR',
      posTokenMap : {},
      beaconUrl : '' //beacon call
    });

    /**
     * @global
     * @typedef {Object} sovrnConfig
     * @property {Array<string>} positions
     * @property {string} [exclude]
     * @property {string} bidUrl
     * @property {number} [timeout=2000]
     * @property {string} [cVar=SOV_VAR]
     * @property {Object} posTokenMap
     * @property {string} [beaconUrl]
     */
    this._config = u.extend(true, this._DefConfig);
    this._excludeList = [];
    this._bidResponses = {};
    this._bidRequest = {
      id : '',
      impid : {}
    };
  }

  /**
   * Reset & Update config specific to this bidder
   * @param {sovrnConfig} oConfig
   */
  getBidPositions(pName: string) {
    // disable this partner in browser without JSON.strigify support
    if(u.isBrowserExcluded(pName, this._excludeList) || !((typeof JSON === 'object') && u.isFunction(JSON.stringify))) {
      return [];
    }
    return this._config.positions;
  }

  /**
   * Reset & Update config specific to this bidder
   * @param {@link _sovrnbid#_config} oConfig
   */
  updateConfig(oConfig: sovrnConfig) {
    this._config = u.extend(true, {}, this._DefConfig, oConfig);

    //compute exclusion list again
    this._excludeList = u.getBrowserExcludeList(this._config.exclude);
  }

  /**
   * getter for qpkey
   * @param {string} pos
   */
  getQPKey(pos: string) {
    return u.isObject(this._config.posTokenMap[pos]) ? this._config.posTokenMap[pos].qpKey : '';
  }

  /**
   * getter for posKey
   * @param {string} pos
   */
  getPosKey(pos: string) {
    return u.isObject(this._config.posTokenMap[pos]) ? this._config.posTokenMap[pos].tagid : '';
  }

  /**
   * getter for cVar
   * @param {string} pos
   */
  getCVar() {
    return this._config.cVar;
  }

  _init(/**posList */) {
    // adding beacon script call in init for now
    return new Promise((resolve, reject) => {
      // TODO: based on requirement beacon scirpt call might happen for all bidding
      if (this._bInited === u.F_INITED) {
        resolve();
        return;
      }

      //Let's do a basic santiy check (bidUrl shdn't be empty etc.,)
      if (!this._config.bidUrl.length || !u.isObject(this._config.posTokenMap)) {
        u.logError(`${this._name} config missing mandatory data`);
        reject();
        return;
      }

      var misPos = this._config.positions.filter(pos => !this._config.posTokenMap[pos]);
      if (misPos.length) {
        u.logError(`${this._name} config positions missing in posTokenMap ${misPos.join(',')}`);
        reject();
        return;
      }

      if (this._config.beaconUrl.length) {
        u.loadJS(this._config.beaconUrl, document, true);
      }
      resolve();
      this._bInited = u.F_INITED;
    });
  }

  /**
  * Process OpenRTF format bid response
  * - stores response in bidResponses for use in renderAd later
  * @access private
  */
  _processBidResponse(posList: Array<string>, resp: any, ret: hbPartnerData) {
    if (!resp || !u.isArray(resp.seatbid) || !resp.seatbid.length ||
        !u.isArray(resp.seatbid[0].bid) || resp.id !== this._bidRequest.id) {
      return ret;
    }
    resp.seatbid[0].bid.forEach(bid => {
      if (bid.impid) {
        this._bidResponses[bid.impid] = bid;
      }
    });

    posList.forEach(pos => {
      let impid = this._bidRequest.impid[pos];
      let res = this._bidResponses[impid];
      let tagid = this.getPosKey(pos);

      //adm - creative, nurl - impression pixel
      if (res && res.price && res.nurl && res.adm) {
        u.logDebug(`${this._name} tagid:${tagid} price:${res.price}`);
        ret[pos].info = `${tagid}|${res.price}`;

        //query param string to pass to OpenX ad call
        ret[pos]._QP[this.getQPKey(pos)] = res.price;
        ret[pos]._CV[this._config.cVar] = impid;
      }
    });
    return ret;
  }

  _getAds(posList: Array<string>, ret: hbPartnerData) {
    this._bidResponses = {};

    //generate unique request id
    this._bidRequest.id  = u.getRandom();

    // build params for bid request
    let params = {
      id : this._bidRequest.id,
      site : {
        domain : location.host,
        page : location.pathname
      },
      imp : []
    };
    params.imp = posList.map(pos => {
      // generate unique id for this impresssion (position)
      this._bidRequest.impid[pos] = u.getRandom();
      return {
        id : this._bidRequest.impid[pos],
        banner : {
          w : this._config.posTokenMap[pos].width,
          h : this._config.posTokenMap[pos].height
        },
        tagid : this.getPosKey(pos)
      };
    });

    let reqParams = {
      data : {
         br : JSON.stringify(params)
      },
      timeout : this._config.timeout,
      logPrefix: posList.join(',')
    };
    return jsonp.JSONP(this._config.bidUrl, reqParams).then(
        data => this._processBidResponse(posList, data, ret),
        msg => { u.logError(`${this._name} Failed to get bids: ${msg || ''}`); return ret; });
  }

  /**
   * get bids for current partner
   * @param {Array<string>} posList - list of ad positions
   * @param {Object.<string, hbPosData>} ret - header bidding default response
   * @return {Promise<Object.<string, hbPosData>>}
   */
  getBids(posList: Array<string>, ret: hbPartnerData) {
    return this._init(posList).then(() => {
      //get bids
      u.logDebug(`${this._name} Init is done. getting ad bids`);
      return this._getAds(posList, ret);
    }, () => {
      u.logError(`${this._name} init failed`);
      return ret;
    });
  }

 /**
  * render ad for this partner
  * @param {window} win - window object
  * @param {window.document} doc - document object
  * @param {string} pos - ad position
  * @param {string} impid - impression id for ad pos
  */
  renderAd(win: window, doc: window.document, pos: string, impid: string) {
    try {
      doc.write(decodeURIComponent(this._bidResponses[impid].adm));
      doc.write(`<img src="${this._bidResponses[impid].nurl}">`);
    } catch(e) {
      u.logDebug(`${this._name} render ad failure ${e.message}`);
    }
  }
}

//export singleton object as default for this module
export default new _sovrnbid();
